/**
 * UTM Attribution Utility
 *
 * Reads UTM parameters from the current URL on first page load and persists
 * them to sessionStorage so they survive navigation within the session.
 * Call `captureUtm()` once at app startup, then `getUtm()` before any form submit.
 */

export interface UtmParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

const STORAGE_KEY = 'pe_utm';

/** Read UTM params from the current URL search string. */
function parseUtmFromUrl(search: string): UtmParams {
  const p = new URLSearchParams(search);
  const result: UtmParams = {};
  const src = p.get('utm_source');
  const med = p.get('utm_medium');
  const cam = p.get('utm_campaign');
  const con = p.get('utm_content');
  const ter = p.get('utm_term');
  if (src) result.utmSource = src;
  if (med) result.utmMedium = med;
  if (cam) result.utmCampaign = cam;
  if (con) result.utmContent = con;
  if (ter) result.utmTerm = ter;
  return result;
}

/**
 * Call once at app startup (e.g. in main.tsx or App.tsx).
 * If UTM params are present in the URL, they are saved to sessionStorage.
 * Existing stored params are NOT overwritten (first-touch attribution).
 */
export function captureUtm(): void {
  if (typeof window === 'undefined') return;
  const fromUrl = parseUtmFromUrl(window.location.search);
  if (!fromUrl.utmSource) return; // Nothing to capture
  // Only store if not already set (first-touch model)
  if (!sessionStorage.getItem(STORAGE_KEY)) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
  }
}

/**
 * Returns the stored UTM params for the current session.
 * Returns an empty object if no UTM params were captured.
 */
export function getUtm(): UtmParams {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as UtmParams;
  } catch {
    return {};
  }
}

/** Clear stored UTM params (e.g. after a successful form submit). */
export function clearUtm(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(STORAGE_KEY);
}
